import { useCallback, useEffect, useState, type ReactNode } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ReportProblemButton } from "@/components/ReportProblemButton";
import { dohvatiGradilista } from "@/lib/gradilista";
import { procitajGreske } from "@/lib/greske";
import {
  hasPermission,
  officeAccess,
  workflow,
  type PeriodLock,
  type DateRange,
} from "@/lib/workflow";

export const controlClass =
  "min-h-[52px] w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-[var(--color-foreground)]";
export const cardClass =
  "rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 space-y-4";
export function errorText(error: unknown): string {
  const errors = procitajGreske(error);
  return (
    [...errors.opcenito, ...Object.values(errors.polja)].join(" ") ||
    "Podaci nisu dostupni. Pokušajte ponovno."
  );
}
export function timestamp(value: string | null): string {
  return value
    ? new Intl.DateTimeFormat("hr-HR", {
        timeZone: "Europe/Zagreb",
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}

/** A key change invalidates old data synchronously, before the next effect. */
export function useResource<T>(
  key: string,
  loader: (signal: AbortSignal) => Promise<T>,
) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    key: string;
    revision: number;
    data?: T;
    error?: string;
  }>({ key: "", revision: -1 });
  useEffect(() => {
    const controller = new AbortController();
    loader(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ key, revision, data });
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setState({ key, revision, error: errorText(error) });
      });
    return () => controller.abort();
  }, [key, loader, revision]);
  const current = state.key === key && state.revision === revision;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : undefined,
    loading: !current,
    reload: useCallback(() => setRevision((r) => r + 1), []),
  };
}

export function Feedback({
  error,
  message,
}: {
  error?: string | null;
  message?: string | null;
}) {
  return (
    <>
      {error && (
        <p
          role="alert"
          className="rounded-lg border-2 border-[var(--color-status-bad-fg)] p-3"
        >
          {error}
          <span className="mt-2 block"><ReportProblemButton message={error} /></span>
        </p>
      )}
      {message && (
        <p role="status" className="rounded-lg border p-3">
          {message}
        </p>
      )}
    </>
  );
}

export function WorkflowNav() {
  const { user } = useAuth();
  const links = [
    ["/ucinak-sati", "Učinak sati", hasPermission(user, "sati", "read")],
    ["/brigade", "Brigade", hasPermission(user, "brigada", "read")],
    ["/taktovi", "Taktovi", hasPermission(user, "takt", "read")],
    ["/operativni-plan", "Operativni plan", hasPermission(user, "takt", "read")],
    [
      "/dani-rasporeda",
      "Dan rasporeda",
      hasPermission(user, "dan_rasporeda", "read"),
    ],
    ["/odobravanje-sati", "Odobravanje sati", officeAccess(user)],
    [
      "/razdoblja",
      "Razdoblja",
      !user?.roles?.some((r) =>
        ["poslovodja", "voditelj", "zamjenik"].includes(r.code),
      ) && hasPermission(user, "razdoblje", "read"),
    ],
    [
      "/zahtjevi-izmjene",
      "Zahtjevi izmjene",
      hasPermission(user, "zahtjev_izmjene", "read"),
    ],
  ] as const;
  return (
    <nav aria-label="Rad na gradilištu" className="flex flex-wrap gap-2">
      {links
        .filter(([, , allowed]) => allowed)
        .map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `inline-flex min-h-[52px] items-center rounded-lg border px-4 font-semibold ${isActive ? "bg-[var(--color-foreground)] text-[var(--color-background)]" : "bg-[var(--color-card)]"}`
            }
          >
            {label}
          </NavLink>
        ))}
    </nav>
  );
}

export function SiteScreen({
  title,
  children,
}: {
  title: string;
  children: (siteId: string) => ReactNode;
}) {
  const sites = useResource(
    "sites",
    useCallback((signal) => dohvatiGradilista(false, signal), []),
  );
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(params.get("gradiliste") ?? "");
  const site = sites.data?.find((s) => s.id === selected) ?? sites.data?.[0];
  return (
    <div className="space-y-5">
      <WorkflowNav />
      <h1 className="text-2xl font-bold">{title}</h1>
      <Feedback error={sites.error} />
      {sites.loading ? (
        <p role="status">Učitavanje gradilišta…</p>
      ) : (
        <label className="block space-y-2">
          Gradilište
          <select
            aria-label="Gradilište"
            className={controlClass}
            value={site?.id ?? ""}
            onChange={(e) => setSelected(e.target.value)}
          >
            {sites.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.naziv}
                {!s.aktivan ? " (neaktivno)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {!sites.loading && !sites.error && !site && (
        <p>Nema dostupnih gradilišta.</p>
      )}
      {site && <div key={site.id}>{children(site.id)}</div>}
    </div>
  );
}

export type LockState = "loading" | "locked" | "open" | "unknown";
export function periodState(
  periods: PeriodLock[],
  day: string,
  end = day,
): LockState {
  if (
    periods.some((p) => {
      const month = `${p.godina}-${String(p.mjesec).padStart(2, "0")}`;
      return (
        !p.otkljucano_at && day.slice(0, 7) <= month && month <= end.slice(0, 7)
      );
    })
  )
    return "locked";
  return periods.length >= 500 ? "unknown" : "open";
}
export function usePeriodLock(site: string, day: string, end = day) {
  const { user } = useAuth();
  const allowed = hasPermission(user, "razdoblje", "read");
  const resource = useResource(
    `${site}:${allowed}`,
    useCallback(
      (signal) =>
        allowed && site
          ? workflow.periods(site, signal)
          : Promise.resolve(null),
      [allowed, site],
    ),
  );
  const state: LockState = !allowed
    ? "unknown"
    : resource.loading
      ? "loading"
      : resource.data
        ? periodState(resource.data, day, end)
        : "unknown";
  return {
    state,
    blocked: state === "locked" || (allowed && state !== "open"),
    reload: resource.reload,
  };
}
export function PeriodLockNotice({ state }: { state: LockState }) {
  const { user } = useAuth();
  // Without period read access the state is always unknown; the server still
  // enforces locks on every write, so the notice would only mislead.
  if (!hasPermission(user, "razdoblje", "read")) return null;
  if (state === "open")
    return (
      <p className="text-sm">
        Prema zadnjoj provjeri zahvaćeno razdoblje nije zaključano.
      </p>
    );
  return (
    <p
      role={state === "loading" ? "status" : "note"}
      className="rounded-lg border-2 p-3 font-semibold"
    >
      {state === "locked"
        ? "Razdoblje je zaključano. Upis i promjene nisu dopušteni."
        : state === "loading"
          ? "Provjera zaključanosti razdoblja…"
          : "Stanje zaključanosti razdoblja nije dostupno. Nije moguće potvrditi da je razdoblje otvoreno."}
    </p>
  );
}

/** Preflight the full affected range, including membership/task history. */
export function useWriteWindow(
  site: string,
  start: string,
  end: string,
  affected?: DateRange[],
) {
  const { user } = useAuth();
  const readPeriods = hasPermission(user, "razdoblje", "read");
  const resource = useResource(
    `${site}:${start}:${end}:${readPeriods}:${JSON.stringify(affected)}`,
    useCallback(
      async (signal) => {
        const [days, periods] = await Promise.all([
          workflow.days({ gradiliste_id: site }, signal),
          readPeriods ? workflow.periods(site, signal) : Promise.resolve(null),
        ]);
        const ranges = affected ?? [{ start, end }];
        const closed = days.some(
          (d) =>
            d.status !== "otvoren" &&
            ranges.some(
              (range) => range.start <= d.datum && d.datum <= range.end,
            ),
        );
        const locked = periods?.some(
          (p) =>
            !p.otkljucano_at &&
            ranges.some(
              (range) =>
                `${p.godina}-${String(p.mjesec).padStart(2, "0")}` >=
                  range.start.slice(0, 7) &&
                `${p.godina}-${String(p.mjesec).padStart(2, "0")}` <=
                  range.end.slice(0, 7),
            ),
        );
        return {
          closed,
          incomplete: days.length >= 500 || (periods?.length ?? 0) >= 500,
          state: locked
            ? ("locked" as const)
            : periods
              ? ("open" as const)
              : ("unknown" as const),
        };
      },
      [site, start, end, readPeriods, affected],
    ),
  );
  const periodBlocked =
    resource.loading ||
    !!resource.error ||
    !!resource.data?.incomplete ||
    resource.data?.state === "locked";
  return {
    state: resource.loading
      ? ("loading" as const)
      : (resource.data?.state ?? "unknown"),
    blocked: periodBlocked || !!resource.data?.closed,
    periodBlocked,
    closed: resource.data?.closed,
    error: resource.error,
    incomplete: resource.data?.incomplete,
    reload: resource.reload,
  };
}
